<template>
    <div class="guide-public-shell">
        <!-- Canonical docs mini-site layout. Rendered inside _LayoutPublic regardless of
             auth state. The dashboard aside has a single Documentation link that routes
             here, and the docs area has its own self-contained nav (header links plus
             GuideSidebar). GuideToc is the right-rail in-page section anchors. No
             VISIBLE page-level heading above .guide-layout so the sticky sidebar sits at
             its intended y=74 from first paint - but a visually-hidden H1 below gives
             search crawlers a real page-level heading to anchor the doc tree on. The H1
             is topic-specific so each ?topic= URL ships a unique page-level heading for
             SEO; each guide component supplies its own h2 sections inside the H1's scope. -->
        <h1 class="visually-hidden">{{ pageH1 }}</h1>
        <div class="guide-layout">
            <GuideSidebar
                :groups="groups"
                :active-topic="activeTopic"
                @navigate="onSidebarNavigate"
            />
            <q-card bordered class="q-pa-lg guide-content-card guide-layout__content" ref="contentCard">
                <transition name="guide-fade" mode="out-in" @after-leave="scrollToTop">
                    <component :is="activeComponent" :key="activeTopic" />
                </transition>
            </q-card>
            <GuideToc :target="contentEl" :target-key="activeTopic" />
        </div>
    </div>
</template>

<script>
import GuideSidebar from '@components/guides/shared/GuideSidebar.vue';
import GuideToc from '@components/guides/shared/GuideToc.vue';
import { guideGroups, guideTopics, defaultGuideTopic } from '../../src/config/guideTopics.js';

// Every guide component referenced in developerTopics.js must be imported here.
// When adding a new topic: add the import + components entry, then add it to developerTopics.js.
// Note: Branding and Roles & Permissions are portal-UI / RBAC content, not API integration -
// they live in Platform Guides (Views/Docs/PlatformConcepts.vue), not here.
import GuideGettingStarted from '@components/guides/GuideGettingStarted.vue';
import GuideQuickStart from '@components/guides/GuideQuickStart.vue';
import GuideAuthenticationScopes from '@components/guides/GuideAuthenticationScopes.vue';
import GuideRolesPermissions from '@components/guides/GuideRolesPermissions.vue';
import GuideEnterpriseSso from '@components/guides/GuideEnterpriseSso.vue';
import GuideSubscriptionLimits from '@components/guides/GuideSubscriptionLimits.vue';
import GuideBranding from '@components/guides/GuideBranding.vue';
import GuidePackages from '@components/guides/GuidePackages.vue';
import GuideDocuments from '@components/guides/GuideDocuments.vue';
import GuideSigners from '@components/guides/GuideSigners.vue';
import GuideRecipientTypes from '@components/guides/GuideRecipientTypes.vue';
import GuideSignerAuthentication from '@components/guides/GuideSignerAuthentication.vue';
import GuideSignerPayments from '@components/guides/GuideSignerPayments.vue';
import GuideSalesTax from '@components/guides/GuideSalesTax.vue';
import GuideAnnotations from '@components/guides/GuideAnnotations.vue';
import GuideTextTags from '@components/guides/GuideTextTags.vue';
import GuideTemplates from '@components/guides/GuideTemplates.vue';
import GuideSigningGroups from '@components/guides/GuideSigningGroups.vue';
import GuideEmbeddedSigning from '@components/guides/GuideEmbeddedSigning.vue';
import GuideWebhooks from '@components/guides/GuideWebhooks.vue';
import GuideHubspot from '@components/guides/GuideHubspot.vue';
import GuideZapier from '@components/guides/GuideZapier.vue';
import GuideMake from '@components/guides/GuideMake.vue';
import GuideAiAgents from '@components/guides/GuideAiAgents.vue';
import GuideErrors from '@components/guides/GuideErrors.vue';
import GuideEvidence from '@components/guides/GuideEvidence.vue';

// Backwards-compat: old URLs used `?topic=getting-started`. Map that key to the new `overview` key
// so existing bookmarks and external links continue to land on the right page.
const LEGACY_TOPIC_ALIASES = {
    'getting-started': 'overview'
};

// Resolved at module load so `data()` can return the final topic without a second-tick update.
// This keeps AppLayout's initial viewkey in sync with the sidebar on first paint.
function resolveInitialTopic() {
    if (typeof window === 'undefined') return defaultGuideTopic;
    var urlParams = new URLSearchParams(window.location.search);
    var topic = urlParams.get('topic');
    if (topic && Object.prototype.hasOwnProperty.call(LEGACY_TOPIC_ALIASES, topic)) {
        topic = LEGACY_TOPIC_ALIASES[topic];
    }
    if (topic && guideTopics.some(function (t) { return t.key === topic; })) {
        return topic;
    }
    return defaultGuideTopic;
}

export default {
    name: 'IntegrationGuidesPage',

    components: {
        GuideSidebar,
        GuideToc,
        GuideGettingStarted,
        GuideQuickStart,
        GuideAuthenticationScopes,
        GuideRolesPermissions,
        GuideEnterpriseSso,
        GuideSubscriptionLimits,
        GuideBranding,
        GuidePackages,
        GuideDocuments,
        GuideSigners,
        GuideRecipientTypes,
        GuideSignerAuthentication,
        GuideSignerPayments,
        GuideSalesTax,
        GuideAnnotations,
        GuideTextTags,
        GuideTemplates,
        GuideSigningGroups,
        GuideEmbeddedSigning,
        GuideWebhooks,
        GuideHubspot,
        GuideZapier,
        GuideMake,
        GuideAiAgents,
        GuideErrors,
        GuideEvidence
    },

    data() {
        return {
            // Resolve from URL at data()-time so the sidebar highlight is correct on first paint.
            activeTopic: resolveInitialTopic(),
            groups: guideGroups,
            topics: guideTopics,
            // The actual DOM element of the content card. Set by `captureContentEl` after mount
            // and after every activeTopic change, so GuideToc can re-scan for h2s in the new guide.
            contentEl: null
        };
    },

    computed: {
        activeComponent() {
            var activeTopic = this.activeTopic;
            var topic = this.topics.find(function (t) { return t.key === activeTopic; });
            return topic ? topic.component : 'GuideGettingStarted';
        },

        // Topic-specific text used inside the visually-hidden page-level H1.
        // Mirrors the server-side heading lookup in GuideTopicSeo.cs so the
        // first-paint H1 (rendered server-side via _guideConfig.initialHeading)
        // and the post-hydration H1 stay identical, avoiding a flicker for
        // crawlers that execute JS.
        activeHeading() {
            var activeTopic = this.activeTopic;
            var topic = this.topics.find(function (t) { return t.key === activeTopic; });
            return topic ? topic.text : 'Integration Guides';
        },

        pageH1() {
            return 'eSign Launchpad - ' + this.activeHeading + ' - Integration Guides';
        }
    },

    mounted() {
        // Back/forward should swap topic in place - read the query param on popstate.
        this._popstateHandler = this.onPopState.bind(this);
        window.addEventListener('popstate', this._popstateHandler);

        // The contentCard ref (a q-card) resolves to a component instance; we want the underlying
        // DOM element so GuideToc can querySelectorAll('h2') on it. Re-capture on every update.
        this.captureContentEl();

        // Sync document.title and canonical on first mount so that if the page
        // was hydrated with a stale topic value (e.g. CDN cache miss), the
        // browser tab and share-link previews reflect the actual topic the
        // user is reading.
        this.syncDocumentMeta();
    },

    watch: {
        // Update document.title and the canonical link whenever the user
        // navigates between topics in-browser (sidebar click → pushState).
        // Initial server response already sets these for the entry URL; this
        // keeps them in sync for in-page navigation so deep-link sharing
        // reflects the topic the user is actually viewing.
        activeTopic() {
            this.syncDocumentMeta();
        }
    },

    updated() {
        this.captureContentEl();
    },

    beforeUnmount() {
        if (this._popstateHandler) {
            window.removeEventListener('popstate', this._popstateHandler);
        }
    },

    methods: {
        // Emitted by GuideSidebar when a public visitor clicks a topic link. The sidebar has
        // already updated the URL via history.pushState, so we just need to swap the active
        // component and scroll back to the top so the new content is visible from its start.
        onSidebarNavigate(key) {
            if (!this.topics.some((t) => t.key === key)) return;
            this.activeTopic = key;
            // Scroll happens in @after-leave so the old content has already faded out.
        },

        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'instant' });
        },

        onPopState() {
            this.activeTopic = resolveInitialTopic();
        },

        // Updates document.title, og:title, and the <link rel="canonical">
        // href to reflect the active topic. Description / og:description are
        // intentionally left as the server-rendered value (per-topic
        // descriptions live in Models/GuideTopicSeo.cs and are authoritative
        // on the server response - duplicating that map client-side would
        // create drift). Title + canonical are the user-visible bits that
        // matter for browser tabs and shared-URL unfurls.
        syncDocumentMeta() {
            var heading = this.activeHeading;
            var topicKey = this.activeTopic;
            var defaultKey = 'overview';

            // Match the server-rendered title chain produced by
            // DocsController → _LayoutPublic → _SeoHead so in-browser topic
            // switches don't shuffle the format compared with the entry URL.
            document.title = heading + ' - Integration Guides - Developer Docs - eSign Launchpad';

            var origin = window.location.origin;
            var canonicalPath = topicKey === defaultKey
                ? '/Docs/IntegrationGuides'
                : '/Docs/IntegrationGuides?topic=' + encodeURIComponent(topicKey);
            var canonicalUrl = origin + canonicalPath;

            var canonicalLink = document.querySelector('link[rel="canonical"]');
            if (canonicalLink) canonicalLink.setAttribute('href', canonicalUrl);

            var ogUrl = document.querySelector('meta[property="og:url"]');
            if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);

            var ogTitle = document.querySelector('meta[property="og:title"]');
            if (ogTitle) ogTitle.setAttribute('content', document.title);
        },

        captureContentEl() {
            // q-card exposes $el via its component ref. Fall back to the raw element for plain divs.
            var ref = this.$refs.contentCard;
            var el = null;
            if (ref) {
                el = ref.$el || (ref instanceof Element ? ref : null);
            }
            if (el !== this.contentEl) {
                this.contentEl = el;
            }
        }
    }
};
</script>

<style scoped>
.q-item--active {
    font-weight: 600;
}

/* Three-column docs layout: GuideSidebar | Content | GuideToc. Same for both auth and public modes.
   GuideSidebar collapses to a toggle below 900px; GuideToc auto-hides below 1200px; stacks below 900px. */
.guide-layout {
    display: flex;
    gap: 32px;
    align-items: flex-start;
}

.guide-layout__content {
    flex: 1 1 auto;
    min-width: 0;  /* prevents long <pre> blocks from forcing the column wider than flex wants */
}

@media (max-width: 900px) {
    .guide-layout {
        flex-direction: column;
        gap: 0;
    }
}
</style>

<style>
/* Public-mode shell styling - applied via `class="guide-public-shell"` in layoutProps.
   Unscoped so the class applied at the host <div> (via v-bind) matches successfully. */
.guide-public-shell {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
}
</style>

<style>
/* Guide content styles - unscoped so they cascade into child guide components */

.guide-content-card h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: #2f3349;
    margin: 0 0 12px 0;
    letter-spacing: -0.01em;
}

.guide-content-card h3 {
    font-size: 1.05rem;
    font-weight: 600;
    color: #455a64;
    margin: 20px 0 8px 0;
}

.guide-content-card p {
    line-height: 1.7;
    color: #455a64;
}

.guide-content-card ul,
.guide-content-card ol {
    padding-left: 24px;
}

.guide-content-card li {
    margin-bottom: 6px;
    line-height: 1.6;
}

/* Dark code blocks */
.guide-content-card .code-block {
    background: #1e293b;
    color: #e2e8f0;
    border: none;
    border-radius: 6px;
    padding: 16px 20px;
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.6;
    font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', 'Consolas', monospace;
}

/* Inline code */
.guide-content-card code:not(.code-block code) {
    background: #f1f5f9;
    color: #0f766e;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.875em;
}

/* Table improvements */
.guide-content-card table thead th {
    background: #f8fafc !important;
    font-weight: 600;
    color: #2f3349;
}

.guide-content-card table tbody tr:hover {
    background: #f8fafc;
}

/* Make sure wide tables scroll horizontally on narrow viewports instead of blowing out the card. */
.guide-content-card table,
.guide-content-card .q-markup-table,
.guide-content-card .q-table {
    max-width: 100%;
}

/* Keep inline-styled legacy tables from overflowing their container. */
.guide-content-card > div[style*="overflow-x"],
.guide-content-card > table {
    max-width: 100%;
    overflow-x: auto;
}

/* Banner left-border accents */
.guide-content-card .q-banner {
    border-radius: 6px;
}

.guide-content-card .q-banner.bg-blue-1 {
    border-left: 3px solid #1976d2;
}

.guide-content-card .q-banner.bg-red-1 {
    border-left: 3px solid #c10015;
}

.guide-content-card .q-banner.bg-amber-1,
.guide-content-card .q-banner.bg-orange-1 {
    border-left: 3px solid #f57f17;
}

.guide-content-card .q-banner.bg-green-1 {
    border-left: 3px solid #6366F1;
}

/* Tighten link styling inside guide content - default Quasar anchors can be underline-less and
   lose affordance. Give them a distinct color and hover underline. */
.guide-content-card a {
    color: #0369a1;
    text-decoration: none;
    border-bottom: 1px solid rgba(3, 105, 161, 0.25);
    transition: border-color 100ms ease-out;
}

.guide-content-card a:hover {
    border-bottom-color: #0369a1;
}

/* Exceptions: links inside q-banners have their own color scheme. */
.guide-content-card .q-banner a {
    color: inherit;
    border-bottom-color: currentColor;
    opacity: 0.9;
}

/* First h2 in a card should not have its top margin collapsed - otherwise it hugs the card edge. */
.guide-content-card > *:first-child {
    margin-top: 0 !important;
}

/* Visual divider that marks the start of the "For Developers and Enterprise Integration" section.
   Signals to the reader that content below is for a different audience (programmatic integrators)
   than the conceptual / portal-UI content above. Each guide uses this by wrapping its developer
   section in <section class="dev-section">...</section>. */
.guide-content-card .dev-section {
    margin-top: 48px;
    padding-top: 36px;
    border-top: 3px double #cbd5e1;
    position: relative;
}

/* Pill label sitting on the top border to further call out the shift. */
.guide-content-card .dev-section::before {
    content: "DEVELOPERS";
    position: absolute;
    top: -11px;
    left: 24px;
    background: #0f172a;
    color: #e0f2fe;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    padding: 4px 10px;
    border-radius: 4px;
}

/* The h2 at the top of the dev section gets an icon prefix and slightly bigger treatment to
   further distinguish it from the conceptual h2s above. */
.guide-content-card .dev-section > h2:first-child {
    font-size: 1.5rem;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 0;
    margin-bottom: 14px;
}

.guide-content-card .dev-section > h2:first-child::before {
    content: "⌨";
    font-size: 1.4rem;
    color: #0369a1;
}

/* Subtle background tint on the whole dev section so the reader feels the shift even when
   scrolled past the top border. Keep it minimal - we don't want it to feel like a different page. */
.guide-content-card .dev-section {
    background: linear-gradient(180deg, rgba(241, 245, 249, 0.4) 0%, rgba(241, 245, 249, 0) 180px);
    padding-left: 24px;
    padding-right: 24px;
    margin-left: -24px;
    margin-right: -24px;
}

/* Topic-switch transition: subtle fade with a short upward drift. `out-in` mode ensures the
   leaving guide fades out before the entering one fades in, preventing layout jump from two
   components being in the DOM simultaneously. Duration is fast enough to feel snappy but slow
   enough that the reader registers the content has changed. */
.guide-fade-enter-active {
    transition: opacity 220ms ease-out, transform 220ms ease-out;
}

.guide-fade-leave-active {
    transition: opacity 140ms ease-in;
}

.guide-fade-enter-from {
    opacity: 0;
    transform: translateY(8px);
}

.guide-fade-leave-to {
    opacity: 0;
}
</style>
